import { useEffect } from 'react';

const SITE = 'https://mikesgaragedoorrepaircypress.com';

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setRobots(content) {
  let el = document.head.querySelector('meta[name="robots"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'robots');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setJsonLd(data) {
  let el = document.head.querySelector('script[data-seo-jsonld]');
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo-jsonld', '');
    document.head.appendChild(el);
  }
  el.textContent = typeof data === 'string' ? data : JSON.stringify(data);
}

/**
 * Dynamically updates document title, meta description, and social tags per page.
 * Restores the default Home meta tags on unmount so navigated-away pages don't leak.
 *
 * @param {object} props
 * @param {string} props.title       - Full <title> / og:title / twitter:title
 * @param {string} props.description - Meta description (≤160 chars recommended)
 * @param {string} [props.path]     - Canonical path (defaults to '/')
 */
export default function Seo({ title, description, path = '/', jsonLd, noindex }) {
  useEffect(() => {
    const previousTitle = document.title;
    const previousDesc = document.head.querySelector('meta[name="description"]')?.getAttribute('content') || '';
    const previousRobots = document.head.querySelector('meta[name="robots"]')?.getAttribute('content') || '';

    const url = `${SITE}${path}`;

    document.title = title;
    setMeta('name', 'title', title);
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setCanonical(url);
    setJsonLd(jsonLd);
    if (noindex) setRobots('noindex, nofollow');

    return () => {
      document.title = previousTitle;
      setMeta('name', 'title', previousTitle);
      setMeta('name', 'description', previousDesc);
      setMeta('property', 'og:title', previousTitle);
      setMeta('property', 'og:description', previousDesc);
      setMeta('property', 'og:url', `${SITE}/`);
      setMeta('name', 'twitter:title', previousTitle);
      setMeta('name', 'twitter:description', previousDesc);
      setCanonical(`${SITE}/`);
      setJsonLd(null);
      if (noindex) setRobots(previousRobots || 'index, follow');
    };
  }, [title, description, path, jsonLd, noindex]);

  return null;
}